import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  MoreVertical,
  Phone,
  Mail,
  MapPin,
  Eye,
  Pencil,
  Trash2,
  Upload,
  FileSpreadsheet,
  Users,
  Briefcase,
  DollarSign,
  AlertCircle,
  Building2,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    is_general_contractor: false,
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async (searchTerm = '') => {
    try {
      const response = await clientApi.getAll(searchTerm || undefined);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchClients(value);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error('Last name is required');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company: formData.company?.trim() || null,
        is_general_contractor: !!formData.is_general_contractor,
        email: formData.email?.trim() || null,
        phone: formData.phone.trim(),
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state || null,
        zip_code: formData.zip_code?.trim() || null,
        notes: formData.notes?.trim() || null,
      };

      if (editingClient) {
        await clientApi.update(editingClient.id, payload);
        toast.success('Client updated');
      } else {
        await clientApi.create(payload);
        toast.success('Client created');
      }
      setShowNewDialog(false);
      setEditingClient(null);
      resetForm();
      fetchClients(search);
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(Array.isArray(msg) ? msg[0]?.msg : msg || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client, e) => {
    e.stopPropagation();
    setEditingClient(client);
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      company: client.company || '',
      is_general_contractor: !!client.is_general_contractor,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      notes: client.notes || '',
    });
    setShowNewDialog(true);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await clientApi.delete(id);
      setClients(clients.filter((c) => c.id !== id));
      toast.success('Client deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    try {
      const response = await clientApi.importCsv(file);
      setImportResult(response.data);
      fetchClients(search);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      company: '',
      is_general_contractor: false,
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      notes: '',
    });
  };

  const openNewDialog = () => {
    setEditingClient(null);
    resetForm();
    setShowNewDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  const NO_COMPANY = '__none__';
  const companyOptions = Array.from(
    new Set(clients.map((c) => (c.company || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const displayedClients = clients.filter((c) => {
    if (companyFilter === 'all') return true;
    if (companyFilter === NO_COMPANY) return !(c.company && c.company.trim());
    return (c.company || '').trim() === companyFilter;
  });

  const clientGroups = (() => {
    const map = new Map();
    displayedClients.forEach((c) => {
      const key = c.company && c.company.trim() ? c.company.trim() : NO_COMPANY;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    const arr = Array.from(map.entries()).map(([key, items]) => ({
      key,
      label: key === NO_COMPANY ? 'No Company / Individual' : key,
      items,
    }));
    arr.sort((a, b) => {
      if (a.key === NO_COMPANY) return 1;
      if (b.key === NO_COMPANY) return -1;
      return a.label.localeCompare(b.label);
    });
    return arr;
  })();

  const renderClientRow = (client) => (
    <TableRow
      key={client.id}
      className="cursor-pointer hover:bg-secondary/50"
      onClick={() => navigate(`/clients/${client.id}`)}
      data-testid={`client-row-${client.id}`}
    >
      <TableCell>
        <div className="font-medium">{client.name}</div>
        {client.company && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5" data-testid={`client-company-${client.id}`}>
            <Building2 className="h-3 w-3" />
            {client.company}
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="space-y-1 text-sm">
          {client.email && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Mail className="h-3 w-3" />
              {client.email}
            </div>
          )}
          {client.phone && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Phone className="h-3 w-3" />
              {client.phone}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        {(client.city || client.state) && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {[client.city, client.state].filter(Boolean).join(', ')}
          </div>
        )}
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="outline" className="rounded-sm">
          {client.lead_count || 0}
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="outline" className="rounded-sm">
          {client.project_count || 0}
        </Badge>
      </TableCell>
      <TableCell className="text-right font-medium">
        {formatCurrency(client.total_value)}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}`); }}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => handleEdit(client, e)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={(e) => handleDelete(client.id, e)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clients</h1>
          <p className="text-muted-foreground">Manage your client database</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => setShowImportDialog(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button className="rounded-sm" onClick={openNewDialog} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Search + Company filter + Group toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={search}
            onChange={handleSearch}
            className="pl-10 rounded-sm"
            data-testid="client-search-input"
          />
        </div>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-full sm:w-[220px] rounded-sm" data-testid="client-company-filter">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <SelectValue placeholder="All Companies" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            <SelectItem value={NO_COMPANY}>No Company / Individual</SelectItem>
            {companyOptions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={groupByCompany ? 'default' : 'outline'}
          className="rounded-sm"
          onClick={() => setGroupByCompany((v) => !v)}
          data-testid="client-group-toggle"
        >
          <Layers className="h-4 w-4 mr-2" />
          Group by company
        </Button>
      </div>

      {/* Clients Table */}
      {clients.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No clients yet</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'No clients match your search' : 'Add your first client to get started'}
            </p>
            {!search && (
              <Button className="rounded-sm" onClick={openNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-center">Leads</TableHead>
                <TableHead className="text-center">Projects</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupByCompany
                ? clientGroups.map((g) => (
                    <React.Fragment key={g.key}>
                      <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                        <TableCell colSpan={7} className="py-2">
                          <span className="flex items-center gap-2 font-semibold text-sm" data-testid={`client-group-${g.key}`}>
                            <Building2 className="h-4 w-4 text-orange-500" />
                            {g.label}
                            <Badge variant="secondary" className="rounded-sm">{g.items.length}</Badge>
                          </span>
                        </TableCell>
                      </TableRow>
                      {g.items.map(renderClientRow)}
                    </React.Fragment>
                  ))
                : displayedClients.map(renderClientRow)}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* New/Edit Client Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="John"
                  data-testid="client-first-name-input"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="Smith"
                  data-testid="client-last-name-input"
                />
              </div>
            </div>
            <div>
              <Label>Company</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="e.g. Acme Builders (GC), optional"
                data-testid="client-company-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_general_contractor}
                onChange={(e) => setFormData({ ...formData, is_general_contractor: e.target.checked })}
                className="h-4 w-4 rounded-sm"
                data-testid="client-gc-toggle"
              />
              This client is a General Contractor (can add tasks via the project share link)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="(555) 123-4567"
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="rounded-sm mt-1"
                  maxLength={2}
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="Additional notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-sm" data-testid="save-client-btn">
                {saving ? 'Saving...' : editingClient ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import Clients from CSV
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with your client data. The file should have columns for:
            </p>
            <div className="bg-secondary/50 p-3 rounded-sm text-sm font-mono">
              first_name, last_name, phone, email, address, city, state, zip_code, notes
            </div>
            <p className="text-xs text-muted-foreground">
              Column names are case-insensitive. Phone is required. Duplicate clients (same phone) will be skipped.
            </p>
            <div className="border-2 border-dashed rounded-sm p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCsv}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="font-medium">Click to upload CSV</p>
                <p className="text-sm text-muted-foreground">or drag and drop</p>
              </label>
            </div>
            {importing && (
              <div className="text-center py-2">
                <span className="loading-spinner" />
                <p className="text-sm text-muted-foreground mt-2">Importing...</p>
              </div>
            )}
            {importResult && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="font-medium text-green-600">✓ Import Complete</p>
                <p className="text-sm">Imported: {importResult.imported}</p>
                <p className="text-sm">Skipped (duplicates): {importResult.skipped}</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600">Errors:</p>
                    <ul className="text-xs text-red-600 list-disc list-inside">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importResult.errors.length > 5 && (
                        <li>...and {importResult.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
